import type { components } from '@fluencelabs/vodopad-admin-api'
import { useInvalidate } from '@refinedev/core'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'
import { serializeQueryWithArrayBrackets } from '@/utils/querySerializer'

type IpAddressType = components['schemas']['IpAddressType']

interface VmRef {
  id: string
  userId: string
  clusterId: string
}

const READY_POLL_INTERVAL_MS = 2500
const READY_POLL_MAX_ATTEMPTS = 24 // ~60s of provisioning

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function usePublicIpActions() {
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()

  const refresh = () => {
    void invalidate({ resource: 'vms', invalidates: ['list'] })
    void invalidate({ resource: 'public-ips', invalidates: ['list'] })
    invalidateAdminCaches(queryClient)
  }

  const attach = async (vmId: string, publicIpId: string) => {
    const result = await client.POST('/v1/admin/vms/{vm_id}/public_ip/add', {
      params: { path: { vm_id: vmId } },
      body: { publicIpId },
    })
    unwrapApiData(result, 'Failed to attach public IP')
    void message.success('Public IP attached')
    refresh()
  }

  const detach = async (vmId: string) => {
    const result = await client.POST('/v1/admin/vms/{vm_id}/public_ip/remove', {
      params: { path: { vm_id: vmId } },
    })
    unwrapApiData(result, 'Failed to detach public IP')
    void message.success('Public IP detached')
    refresh()
  }

  // Poll the IP until it reaches `ready` — vodopad allocates the address
  // asynchronously, and add rejects an IP that isn't ready yet.
  const waitForReady = async (ipId: string) => {
    for (let i = 0; i < READY_POLL_MAX_ATTEMPTS; i++) {
      const poll = await client.GET('/v1/admin/public_ips', {
        params: { query: { ids: [ipId], page: 1, perPage: 1 } },
        querySerializer: serializeQueryWithArrayBrackets,
      })
      const ip = poll.data?.items?.[0]
      if (ip?.status === 'ready') return true
      if (ip?.status === 'failed' || ip?.status === 'removed') {
        throw new Error(`Public IP provisioning ${ip.status}`)
      }
      await sleep(READY_POLL_INTERVAL_MS)
    }
    return false
  }

  const createAndAttach = async (
    vm: VmRef,
    input: { name: string; addressType: IpAddressType },
  ): Promise<{ attached: boolean }> => {
    const created = unwrapApiData(
      await client.POST('/v1/admin/public_ips', {
        body: {
          userId: vm.userId,
          clusterId: vm.clusterId,
          name: input.name,
          addressType: input.addressType,
        },
      }),
      'Failed to create public IP',
    )

    if (!(await waitForReady(created.id))) {
      refresh()
      return { attached: false }
    }

    await attach(vm.id, created.id)
    return { attached: true }
  }

  return { attach, detach, createAndAttach }
}
